import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart,
  Truck,
  Users,
  FileText,
  Download,
  Calendar,
  Beef,
  Salad,
  Package,
  CreditCard,
  Utensils,
  Scissors
} from 'lucide-react';
import api from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';

export default function MonthlyReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Son 12 ay için seçenekler oluştur
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/monthly-summary?month=${selectedMonth}`);
      setReport(response.data);
    } catch (error) {
      toast.error('Rapor yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="monthly-report-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-primary" />
              Aylık Özet Rapor
            </h1>
            <p className="text-muted-foreground">Detaylı aylık performans analizi</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px]" data-testid="month-selector">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {report && (
          <>
            {/* Özet Kartlar */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Toplam Ciro</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {formatCurrency(report.summary.total_revenue)}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        {report.summary.working_days} gün çalışıldı
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-600 font-medium">Toplam Maliyet</p>
                      <p className="text-2xl font-bold text-orange-800">
                        {formatCurrency(report.summary.total_cost)}
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        Günlük ort: {formatCurrency(report.summary.total_cost / (report.summary.working_days || 1))}
                      </p>
                    </div>
                    <ShoppingCart className="w-10 h-10 text-orange-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className={`bg-gradient-to-br ${report.summary.net_profit >= 0 ? 'from-green-50 to-green-100 border-green-200' : 'from-red-50 to-red-100 border-red-200'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${report.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Net Kar/Zarar
                      </p>
                      <p className={`text-2xl font-bold ${report.summary.net_profit >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                        {report.summary.net_profit >= 0 ? '+' : ''}{formatCurrency(report.summary.net_profit)}
                      </p>
                      <p className={`text-xs mt-1 ${report.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Kar Marjı: %{report.summary.profit_margin}
                      </p>
                    </div>
                    {report.summary.net_profit >= 0 ? (
                      <TrendingUp className="w-10 h-10 text-green-400" />
                    ) : (
                      <TrendingDown className="w-10 h-10 text-red-400" />
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Günlük Ortalama</p>
                      <p className="text-2xl font-bold text-purple-800">
                        {formatCurrency(report.summary.daily_average)}
                      </p>
                      <p className="text-xs text-purple-600 mt-1">
                        ciro / gün
                      </p>
                    </div>
                    <Calendar className="w-10 h-10 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Maliyet Dağılımı */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Gider Kategorileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Gider Dağılımı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(report.expense_categories).map(([cat, amount]) => {
                      const percentage = report.cost_breakdown.expenses > 0 
                        ? (amount / report.cost_breakdown.expenses * 100).toFixed(1) 
                        : 0;
                      return (
                        <div key={cat} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-primary/60"></div>
                            <span className="text-sm">{cat}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{formatCurrency(amount)}</Badge>
                            <span className="text-xs text-muted-foreground w-12 text-right">%{percentage}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="border-t pt-3 mt-3 flex items-center justify-between font-semibold">
                      <span>Toplam Gider</span>
                      <Badge>{formatCurrency(report.cost_breakdown.expenses)}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Maliyet Özeti */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ShoppingCart className="w-5 h-5 text-primary" />
                    Maliyet Özeti
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Beef className="w-5 h-5 text-red-600" />
                        <span>Et/Tavuk</span>
                      </div>
                      <span className="font-semibold text-red-700">{formatCurrency(report.cost_breakdown.meat)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Salad className="w-5 h-5 text-green-600" />
                        <span>Sebze/Manav</span>
                      </div>
                      <span className="font-semibold text-green-700">{formatCurrency(report.cost_breakdown.vegetables)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-blue-600" />
                        <span>Genel Ürünler</span>
                      </div>
                      <span className="font-semibold text-blue-700">{formatCurrency(report.cost_breakdown.general)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-orange-600" />
                        <span>Sevkiyat</span>
                      </div>
                      <span className="font-semibold text-orange-700">{formatCurrency(report.cost_breakdown.transfer)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                        <span>Giderler</span>
                      </div>
                      <span className="font-semibold text-purple-700">{formatCurrency(report.cost_breakdown.expenses)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* En Çok Alınan Ürünler */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Et/Tavuk */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Beef className="w-5 h-5 text-red-500" />
                    En Çok Alınan Et/Tavuk
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.top_meat_products.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Bu ay et/tavuk alımı yok</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün</TableHead>
                          <TableHead className="text-right">Miktar</TableHead>
                          <TableHead className="text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.top_meat_products.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell className="text-right">{formatNumber(item.quantity)} kg</TableCell>
                            <TableCell className="text-right text-red-600 font-semibold">
                              {formatCurrency(item.cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Sebze/Manav */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Salad className="w-5 h-5 text-green-500" />
                    En Çok Alınan Sebze/Manav
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.top_vegetables.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Bu ay sebze alımı yok</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün</TableHead>
                          <TableHead className="text-right">Miktar</TableHead>
                          <TableHead className="text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.top_vegetables.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell className="text-right">{formatNumber(item.quantity)} {item.unit}</TableCell>
                            <TableCell className="text-right text-green-600 font-semibold">
                              {formatCurrency(item.cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sevkiyat ve Diğer Bilgiler */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* En Çok Sevk Edilen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Truck className="w-5 h-5 text-orange-500" />
                    En Çok Sevk Edilen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.top_transfers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Bu ay sevkiyat yok</p>
                  ) : (
                    <div className="space-y-2">
                      {report.top_transfers.slice(0, 8).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                          <span className="text-sm truncate flex-1">{item.name}</span>
                          <Badge variant="outline">{formatNumber(item.quantity)}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Personel Yemeği */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Utensils className="w-5 h-5 text-amber-500" />
                    Personel Yemeği
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-3xl font-bold text-amber-700">{report.staff_meals.total_meals}</p>
                      <p className="text-sm text-amber-600">Toplam Öğün</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-3xl font-bold text-amber-700">{report.staff_meals.total_persons}</p>
                      <p className="text-sm text-amber-600">Toplam Kişi</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hazırlık Mutfağı */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Scissors className="w-5 h-5 text-rose-500" />
                    Hazırlık Mutfağı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
                      <span className="text-sm">İşlenen Hammadde</span>
                      <Badge variant="secondary">{formatNumber(report.processing.total_processed)} kg</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span className="text-sm">Çıkan Ürün</span>
                      <Badge className="bg-green-600">{formatNumber(report.processing.total_output)} kg</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                      <span className="text-sm">Fire</span>
                      <Badge variant="outline" className="text-orange-600">
                        {formatNumber(report.processing.total_waste)} kg (%{report.processing.waste_percentage})
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
